import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Briefcase, MapPin, Users, ArrowRight, X, Save, Trash2, Search, List, LayoutGrid, Linkedin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ConfirmationModal from './ConfirmationModal';
import LinkedInModal from './LinkedInModal';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function JobsDashboard({ onViewJob }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // LinkedIn Modal State
    const [showLinkedInModal, setShowLinkedInModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    // Create Job Form State
    const [newJob, setNewJob] = useState({
        title: "",
        department: "Engineering",
        location: "Remote",
        description: "",
        requirements: ""
    });

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            const res = await axios.get(`${API_BASE}/jobs`);
            setJobs(res.data);
        } catch (err) {
            console.error("Failed to fetch jobs", err);
            toast.error("Could not load jobs");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateJob = async (e) => {
        e.preventDefault();
        try {
            // Convert newline requirements to list
            const payload = {
                ...newJob,
                requirements: newJob.requirements.split('\n').filter(r => r.trim())
            };

            await axios.post(`${API_BASE}/jobs`, payload);
            toast.success("Job created successfully!");
            setShowCreateModal(false);
            setNewJob({ title: "", department: "Engineering", location: "Remote", description: "", requirements: "" });
            // Optimistic update or refetch
            fetchJobs();
        } catch (err) {
            console.error(err);
            toast.error("Failed to create job");
        }
    };

    const [deleteJobId, setDeleteJobId] = useState(null);

    const handleDeleteClick = (id, e) => {
        e.stopPropagation();
        setDeleteJobId(id);
    };

    const handleLinkedInClick = (job, e) => {
        e.stopPropagation();
        setSelectedJob(job);
        setShowLinkedInModal(true);
    };

    const confirmDeleteJob = async () => {
        if (!deleteJobId) return;
        try {
            await axios.delete(`${API_BASE}/jobs/${deleteJobId}`);
            toast.success("Job deleted");
            setJobs(prev => prev.filter(j => j._id !== deleteJobId));
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete job");
        }
        setDeleteJobId(null);
    };

    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("grid");

    // Filter jobs
    const filteredJobs = jobs.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-20 text-center"><div className="animate-spin w-8 h-8 border-2 border-brand-600 rounded-full mx-auto"></div></div>;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="text-center py-8">
                <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4 tracking-tight">
                    Job Openings
                </h1>
                <p className="text-slate-400 text-lg">Manage your active requisitions</p>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative group flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search jobs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64 pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                        />
                    </div>

                    <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1 dark:bg-slate-800 dark:border-slate-700">
                        <button
                            onClick={() => setViewMode('list')}
                            className={clsx(
                                "p-1.5 rounded-md transition-all",
                                viewMode === 'list' ? "bg-slate-100 text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            )}
                            title="List View"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('grid')}
                            className={clsx(
                                "p-1.5 rounded-md transition-all",
                                viewMode === 'grid' ? "bg-slate-100 text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            )}
                            title="Grid View"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm whitespace-nowrap"
                    >
                        <Plus className="w-5 h-5" /> Post Job
                    </button>
                </div>
            </div>

            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredJobs.map(job => (
                        <motion.div
                            key={job._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all group relative"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 rounded-lg">
                                    <Briefcase className="w-6 h-6" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${job.status === 'open'
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                        }`}>
                                        {job.status.toUpperCase()}
                                    </span>

                                    {/* LinkedIn Button */}
                                    <button
                                        onClick={(e) => handleLinkedInClick(job, e)}
                                        className="p-1.5 text-slate-400 hover:text-[#0077b5] hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Broadcast to LinkedIn"
                                    >
                                        <Linkedin className="w-4 h-4" />
                                    </button>

                                    <button
                                        onClick={(e) => handleDeleteClick(job._id, e)}
                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete Job"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">{job.title}</h3>

                            <div className="space-y-2 mb-6">
                                <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                    <Users className="w-4 h-4 mr-2" /> {job.department}
                                </div>
                                <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                    <MapPin className="w-4 h-4 mr-2" /> {job.location}
                                </div>
                            </div>

                            <button
                                onClick={() => onViewJob(job)}
                                className="w-full py-2 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg font-medium text-sm hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2 group-hover:text-brand-600 dark:group-hover:text-brand-400"
                            >
                                View Details <ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-300">Job Title</th>
                                <th className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-300">Department</th>
                                <th className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-300">Location</th>
                                <th className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                                <th className="px-6 py-4 font-semibold text-slate-700 dark:text-slate-300 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {filteredJobs.map(job => (
                                <tr key={job._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group cursor-pointer" onClick={() => onViewJob(job)}>
                                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">{job.title}</td>
                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{job.department}</td>
                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{job.location}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${job.status === 'open'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                            }`}>
                                            {job.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {/* LinkedIn Button in List View */}
                                            <button
                                                onClick={(e) => handleLinkedInClick(job, e)}
                                                className="p-1.5 text-slate-400 hover:text-[#0077b5] hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Broadcast to LinkedIn"
                                            >
                                                <Linkedin className="w-4 h-4" />
                                            </button>

                                            <button
                                                onClick={(e) => handleDeleteClick(job._id, e)}
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                            <ArrowRight className="w-4 h-4 text-slate-400" />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredJobs.length === 0 && (
                <div className="col-span-full py-20 text-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 font-medium">No jobs found.</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="text-brand-600 font-medium hover:underline mt-2"
                    >
                        Create your first job posting
                    </button>
                </div>
            )}

            {/* Create Job Modal */}
            <AnimatePresence>
                {showCreateModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setShowCreateModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Create New Job</h3>
                                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-red-500"><X className="w-5 h-5" /></button>
                            </div>

                            <form onSubmit={handleCreateJob} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Job Title</label>
                                        <input
                                            required
                                            className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                            value={newJob.title}
                                            onChange={e => setNewJob({ ...newJob, title: e.target.value })}
                                            placeholder="e.g. Senior Product Designer"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Department</label>
                                        <select
                                            className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none"
                                            value={newJob.department}
                                            onChange={e => setNewJob({ ...newJob, department: e.target.value })}
                                        >
                                            <option>Engineering</option>
                                            <option>Product</option>
                                            <option>Design</option>
                                            <option>Marketing</option>
                                            <option>Sales</option>
                                            <option>HR</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
                                    <input
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                                        value={newJob.location}
                                        onChange={e => setNewJob({ ...newJob, location: e.target.value })}
                                        placeholder="e.g. Remote, New York, London"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
                                    <textarea
                                        required
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none min-h-[100px]"
                                        value={newJob.description}
                                        onChange={e => setNewJob({ ...newJob, description: e.target.value })}
                                        placeholder="Enter the full job description..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Key Requirements (one per line)</label>
                                    <textarea
                                        className="w-full p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none min-h-[100px]"
                                        value={newJob.requirements}
                                        onChange={e => setNewJob({ ...newJob, requirements: e.target.value })}
                                        placeholder="- 5+ years of React experience&#10;- Knowledge of AWS&#10;- Python proficiency"
                                    />
                                </div>
                            </form>

                            <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-900/50">
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateJob}
                                    className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold flex items-center gap-2 shadow-lg shadow-brand-200 dark:shadow-none"
                                >
                                    <Save className="w-4 h-4" /> Create Job
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ConfirmationModal
                isOpen={!!deleteJobId}
                onClose={() => setDeleteJobId(null)}
                onConfirm={confirmDeleteJob}
                title="Delete Job?"
                message="Are you sure you want to delete this job? All applications linked to this job will also be unlinked or deleted."
                confirmText="Delete Job"
            />

            <LinkedInModal
                isOpen={showLinkedInModal}
                onClose={() => setShowLinkedInModal(false)}
                job={selectedJob}
            />
        </div >
    );
}
